<template>
  <div class="min-h-screen bg-gray-50 touch-scroll animate__animated animate__fadeIn">
    <!-- Header with Back Button - Sticky -->
    <div class="bg-white shadow-sm border-b sticky top-0 z-20">
      <div class="max-w-7xl mx-auto px-2 sm:px-4">
        <div class="flex items-center justify-between h-14">
          <!-- Back Button -->
          <button 
            @click="$router.push('/dashboard')"
            class="flex items-center space-x-2 px-2 py-1.5 text-gray-600 hover:text-gray-900 transition-colors touch-manipulation"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span class="text-sm font-medium">Back</span>
          </button>

          <div class="flex items-center space-x-2">
            <h1 class="text-xl font-bold text-gray-900">Point of Sale</h1>
            <!-- Offline Status Indicator -->
            <div v-if="!isOnline" class="flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-md text-xs animate__animated animate__pulse animate__infinite">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM4 10a6 6 0 1112 0A6 6 0 014 10z" clip-rule="evenodd"/>
              </svg>
              <span>Offline</span>
            </div>
            <!-- Sync Status -->
            <div v-if="syncInProgress" class="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
              <div class="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              <span>Syncing</span>
            </div>
            <!-- Queue Status -->
            <div v-if="offlineQueueStats.pending > 0" class="flex items-center space-x-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs animate__animated animate__bounce animate__infinite animate__slow">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span>{{ offlineQueueStats.pending }} pending</span>
            </div>
          </div>
          
          <!-- Compact Customer Selection -->
          <button 
            @click="showCustomerModal = true"
            class="flex items-center space-x-1.5 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all duration-200 text-sm touch-manipulation hover:scale-105"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
            <div class="flex flex-col items-start">
              <span class="max-w-24 truncate">{{ selectedCustomer ? selectedCustomer.name : 'Walk-in' }}</span>
              <div class="flex items-center space-x-1">
                <span v-if="selectedCustomer && selectedCustomer.customer_type" class="text-xs text-gray-500">
                  {{ selectedCustomer.customer_type }}
                </span>
                <span v-if="isGstEnabled && isInterState" class="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                  IGST
                </span>
                <span v-else-if="isGstEnabled && selectedCustomer" class="text-xs bg-green-100 text-green-800 px-1 rounded">
                  CGST+SGST
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="content-area-pos pb-40 lg:pb-8">
      <div class="max-w-7xl mx-auto px-2 sm:px-4 py-3">
        
        <!-- Desktop Layout: Split view on lg+ screens -->
        <div class="lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0 space-y-3">
          
          <!-- Left Column: Products (2/3 width on desktop, full width on mobile) -->
          <div class="lg:col-span-2 space-y-3">
            
            <!-- Product Search Bar -->
            <div class="bg-white rounded-lg shadow-sm border animate__animated animate__slideInDown relative z-50" style="animation-delay: 0.1s">
        <div class="p-3">
          <div class="flex space-x-2">
            <div class="flex-1 relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                </svg>
              </div>
              <input
                v-model="searchQuery"
                @input="searchProducts"
                @keydown.enter="selectFirstProduct"
                @keydown.arrow-down="navigateSearchResults(1)"
                @keydown.arrow-up="navigateSearchResults(-1)"
                type="text"
                placeholder="Search by product name, barcode, or short code..."
                class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
              <!-- Search Results Dropdown -->
              <div v-if="searchResults.length > 0 && searchQuery" key="search-dropdown" class="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-[60] max-h-80 overflow-y-auto animate__animated animate__slideInDown animate__faster">
                <div 
                  v-for="(product, index) in searchResults" 
                  :key="product._id"
                  @click="(!allowOverselling && (product.stock_quantity || 0) <= 0) ? null : (addToCart(product), clearSearch())"
                  :class="{ 
                    'bg-blue-50': index === selectedSearchIndex,
                    'opacity-50 cursor-not-allowed': !allowOverselling && (product.stock_quantity || 0) <= 0,
                    'cursor-pointer hover:bg-gray-50 transition-colors': allowOverselling || (product.stock_quantity || 0) > 0
                  }"
                  class="p-2 border-b border-gray-100 last:border-b-0"
                >
                  <div class="flex justify-between items-center">
                    <div class="flex-1">
                      <div class="font-medium text-gray-900 text-sm">{{ product.name }}</div>
                      <div class="text-xs text-gray-500 mt-0.5">
                        <span class="font-mono">{{ product.barcode || product.short_code }}</span>
                        <span class="ml-2" :class="!allowOverselling && (product.stock_quantity || 0) <= 0 ? 'text-red-500 font-medium' : ''">
                          Stock: {{ product.stock_quantity || 0 }}
                          <span v-if="!allowOverselling && (product.stock_quantity || 0) <= 0" class="ml-1 text-red-500">• Out of Stock</span>
                        </span>
                      </div>
                    </div>
                    <div class="text-sm font-semibold text-green-600">₹{{ formatPrice(product.pricing?.sale_price_inc_tax || 0) }}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Barcode Scanner Button -->
            <button 
              @click="toggleBarcodeScanner"
              class="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              title="Scan Barcode"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 5H1V3C1 2.448 1.448 2 2 2H4V4H3V5ZM1 11H3V13H1V11ZM1 7H3V9H1V7ZM3 17H1V15H3V17ZM3 21H1V19C1 18.448 1.448 18 2 18H4V20H3V21ZM21 5H23V3C23 2.448 22.552 2 22 2H20V4H21V5ZM23 11H21V13H23V11ZM23 7H21V9H23V7ZM21 17H23V15H21V17ZM21 21H23V19C23 18.448 22.552 18 22 18H20V20H21V21ZM5 7H7V17H5V7ZM9 7H11V17H9V7ZM13 7H15V17H13V7ZM17 7H19V17H17V7Z"/>
              </svg>
            </button>
            
            <!-- Products Modal Button -->
            <button 
              @click="showProductsModal = true"
              class="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              title="Browse Products"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Cart Items -->
      <div class="bg-white rounded-lg shadow-sm border mb-6 flex flex-col h-full">
        <div class="p-4 border-b flex-shrink-0">
          <h2 class="text-lg font-semibold text-gray-900">Cart Items</h2>
        </div>
        
        <div v-if="cartItems.length === 0" key="empty-cart" class="p-8 text-center animate__animated animate__fadeIn flex-1 flex flex-col justify-center">
          <svg class="w-16 h-16 mx-auto text-gray-300 mb-4 animate__animated animate__pulse animate__infinite" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6a2 2 0 114 0v1H8V6zm0 3a1 1 0 012 0 1 1 0 11-2 0z" clip-rule="evenodd"/>
          </svg>
          <p class="text-gray-500">No items in cart</p>
          <p class="text-sm text-gray-400 mt-1">Search for products above to add them to your cart</p>
        </div>

        <div v-else key="cart-with-items" class="flex-1 overflow-y-auto max-h-[calc(100vh-400px)] lg:max-h-[calc(100vh-250px)]">
          <div class="space-y-3 p-4">
          <div 
            v-for="(item, index) in cartItems" 
            :key="item.id"
            class="bg-gray-50 rounded-lg border p-3 animate__animated animate__slideInUp"
            :style="`animation-delay: ${index * 0.1}s`"
          >
            <!-- Cart Item Layout - Matching Mockup -->
            <div class="flex items-center justify-between">
              <!-- Product Info & Remove Button -->
              <div class="flex-1">
                <div class="flex items-start justify-between">
                  <div>
                    <h3 class="font-semibold text-gray-900 text-base leading-tight">
                      {{ item.name }} - <span class="text-sm text-gray-600 font-mono">[{{ item.barcode }}]</span>
                    </h3>
                  </div>
                  <button 
                    @click="removeFromCart(item.id)"
                    class="text-gray-400 hover:text-red-500 transition-all duration-200 hover:scale-110 active:scale-95 ml-2"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Controls Row -->
            <div class="mt-3 flex items-center justify-between">
              <!-- Quantity Controls (Inline Layout) -->
              <div class="flex flex-col items-center">
                <label class="text-xs text-gray-500 mb-1">Qty - <span class="font-bold text-gray-700">{{ item.unit || 'piece' }}</span></label>
                <div class="flex items-center space-x-1">
                  <button 
                    @click="updateQuantity(item.id, getDecrementValue(item))"
                    class="w-8 h-8 bg-gray-300 hover:bg-gray-400 rounded flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                  >
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                  <input 
                    v-model="item.quantity"
                    @input="updateQuantity(item.id, parseFloat(item.quantity) || 0)"
                    @focus="openQuantityKeypadForItem(item)"
                    type="number"
                    :min="item.decimal_allowed ? '0.01' : '1'"
                    :step="item.decimal_allowed ? getStepValue() : '1'"
                    readonly
                    class="w-12 h-8 text-center border border-gray-300 rounded px-1 text-sm font-medium bg-white cursor-pointer"
                  >
                  <button 
                    @click="updateQuantity(item.id, getIncrementValue(item))"
                    class="w-8 h-8 bg-gray-300 hover:bg-gray-400 rounded flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                  >
                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Tax Selector -->
              <div v-if="isGstEnabled" class="flex flex-col items-center">
                <label class="text-xs text-gray-500 mb-1">GST</label>
                <select 
                  v-model="item.tax_rate"
                  @change="updateItemTax(item)"
                  class="w-20 h-8 text-center border border-yellow-300 rounded px-1 text-sm font-medium bg-yellow-50 text-yellow-700"
                >
                  <option v-for="option in taxRateOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
              
              <!-- Price Input -->
              <div class="flex flex-col items-center">
                <label class="text-xs text-gray-500 mb-1">Price</label>
                <button 
                  v-if="isGstEnabled"
                  @click="openPriceModal(item)"
                  class="w-20 h-8 text-center border border-green-300 rounded px-1 text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                >
                  ₹{{ formatPrice(item.price) }}
                </button>
                <input 
                  v-else
                  v-model.number="item.price"
                  @input="updateItemPrice(item)"
                  @focus="openPriceKeypadForItem(item)"
                  type="number"
                  step="0.01"
                  readonly
                  class="w-20 h-8 text-center border border-green-300 rounded px-1 text-sm font-medium bg-green-50 text-green-700 cursor-pointer"
                >
              </div>
              
              <!-- Total -->
              <div class="flex flex-col items-center">
                <label class="text-xs text-gray-500 mb-1">Total</label>
                <div class="w-20 h-8 flex items-center justify-center text-sm font-bold text-gray-900 bg-gray-100 border border-gray-300 rounded">
                  ₹{{ itemTotal(item) }}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Cart Summary, Payment Buttons (1/3 width on desktop, full width on mobile) -->
    <div class="lg:col-span-1 space-y-3">
      <!-- Cart Summary -->
      <div v-if="cartItems.length > 0" key="cart-summary" class="bg-white rounded-lg shadow-sm border mb-3 animate__animated animate__slideInUp" style="animation-delay: 0.3s">
        <div class="p-3">
          <!-- Discount Section -->
          <div class="mb-3">
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Disc</label>
              <div class="flex space-x-1">
                <button 
                  @click="discountType = 'percentage'"
                  class="px-2 py-1 text-xs rounded-md transition-colors"
                  :class="discountType === 'percentage' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'"
                >
                  %
                </button>
                <button 
                  @click="discountType = 'amount'"
                  class="px-2 py-1 text-xs rounded-md transition-colors"
                  :class="discountType === 'amount' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'"
                >
                  ₹
                </button>
              </div>
              <input 
                v-model.number="discount"
                type="number"
                min="0"
                :max="discountType === 'percentage' ? 100 : subtotal"
                class="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="discountType === 'percentage' ? 'Enter %' : 'Enter amount'"
              >
            </div>
          </div>
          
          <!-- Totals -->
          <div class="space-y-2 border-t pt-4">
            <div class="flex justify-between text-sm">
              <span class="text-gray-600">Subtotal:</span>
              <span class="font-medium">₹{{ subtotal }}</span>
            </div>
            <div v-if="discountAmount > 0" class="flex justify-between text-sm">
              <span class="text-gray-600">Discount:</span>
              <span class="font-medium text-green-600">-₹{{ discountAmount }}</span>
            </div>
            <div v-if="gstAmount > 0" class="space-y-1">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">GST:</span>
                <span class="font-medium">₹{{ formatPrice(gstAmount) }}</span>
              </div>
              <!-- GST Breakdown -->
              <div v-if="isGstEnabled" class="pl-4 space-y-1 text-xs text-gray-500">
                <div v-if="isInterState && gstBreakdown.igst > 0" class="flex justify-between">
                  <span>IGST:</span>
                  <span>₹{{ formatPrice(gstBreakdown.igst) }}</span>
                </div>
                <div v-if="!isInterState">
                  <div v-if="gstBreakdown.cgst > 0" class="flex justify-between">
                    <span>CGST:</span>
                    <span>₹{{ formatPrice(gstBreakdown.cgst) }}</span>
                  </div>
                  <div v-if="gstBreakdown.sgst > 0" class="flex justify-between">
                    <span>SGST:</span>
                    <span>₹{{ formatPrice(gstBreakdown.sgst) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-between text-lg font-bold border-t pt-2">
              <span>Total:</span>
              <span class="text-green-600">₹{{ total }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Sale Buttons - Sticky at bottom -->
      <div v-if="cartItems.length > 0" key="checkout-section" class="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-30 lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:border-t-0 lg:shadow-none lg:z-auto animate__animated animate__slideInUp" style="animation-delay: 0.5s">
        <div class="p-3 lg:p-0">
          
          <!-- Streamlined Printer & Auto Print Controls -->
          <div class="mb-3 flex items-center justify-between p-2 bg-gray-50 rounded-lg border">
            <!-- Auto Print Toggle -->
            <div class="flex items-center space-x-2">
              <label class="text-sm font-medium text-gray-700">Auto Print:</label>
              <button 
                @click="toggleAutoprint"
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                :class="autoprint ? 'bg-blue-600' : 'bg-gray-300'"
              >
                <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                      :class="autoprint ? 'translate-x-4' : 'translate-x-0.5'">
                </span>
              </button>
            </div>
            
            <!-- One-Click Printer Connection -->
            <div 
              @click="togglePrinterConnection"
              class="flex items-center space-x-2 px-3 py-1.5 rounded-full cursor-pointer transition-all duration-200 touch-manipulation"
              :class="printerStatus.isConnected 
                ? 'bg-green-100 hover:bg-green-200 text-green-800' 
                : 'bg-red-100 hover:bg-red-200 text-red-800'"
              :title="printerStatus.isConnected 
                ? `Connected to: ${printerStatus.deviceName}` 
                : 'Printer not connected - Click to connect'"
            >
              <!-- Printer Icon -->
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zM5 14a1 1 0 001 1h8a1 1 0 001-1v-3H5v3z" clip-rule="evenodd"/>
              </svg>
              
              <!-- Status Indicator Dot -->
              <div 
                class="w-2 h-2 rounded-full animate-pulse"
                :class="printerStatus.isConnected ? 'bg-green-500' : 'bg-red-500'"
              ></div>
              
              <!-- Status Text -->
              <span class="text-xs font-medium">
                {{ printerStatus.isConnected ? 'Connected' : 'Connect Printer' }}
              </span>
            </div>
          </div>

          <div class="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
        <button 
          v-if="selectedCustomer && selectedCustomer.id !== 'walk-in'"
          @click="processSale('credit')"
          :disabled="!selectedCustomer.allow_credit"
          class="flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 min-w-0 flex-1"
          :class="selectedCustomer.allow_credit 
            ? 'bg-orange-500 text-white hover:bg-orange-600' 
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
        >
          <svg class="w-8 h-8 mb-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
          </svg>
          <span class="text-xs font-medium text-center">Credit</span>
          <span v-if="!selectedCustomer.allow_credit" class="text-xs text-gray-500">Not Allowed</span>
        </button>
        
        <button 
          @click="processSale('cash')"
          class="flex flex-col items-center justify-center p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 hover:scale-105 active:scale-95 min-w-0 flex-1"
        >
          <svg class="w-8 h-8 mb-1" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm5 3a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM7.5 11a1 1 0 100-2 1 1 0 000 2zm5.5-1a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
          </svg>
          <span class="text-xs font-medium text-center">Cash</span>
        </button>
        
        <button 
          @click="processSale('upi')"
          class="flex flex-col items-center justify-center p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 hover:scale-105 active:scale-95 min-w-0 flex-1"
        >
          <svg class="w-8 h-8 mb-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm2 2a1 1 0 000 2h.01a1 1 0 100-2H5zm3 0a1 1 0 000 2h3a1 1 0 100-2H8z" clip-rule="evenodd"/>
          </svg>
          <span class="text-xs font-medium text-center">UPI</span>
        </button>
        
        <button 
          @click="processSale('card')"
          class="flex flex-col items-center justify-center p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 hover:scale-105 active:scale-95 min-w-0 flex-1"
        >
          <svg class="w-8 h-8 mb-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2v2h12V6H4zm12 4H4v4h12v-4z" clip-rule="evenodd"/>
          </svg>
          <span class="text-xs font-medium text-center">Card</span>
        </button>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>

    <!-- Offline Queue Status - Sticky under header -->
    <div v-if="offlineQueueStats.total > 0 || !isOnline" class="bg-white shadow-sm border-b sticky top-14 z-10">
      <div class="max-w-7xl mx-auto px-2 sm:px-4 py-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="flex items-center space-x-2">
              <svg v-if="!isOnline" class="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM4 10a6 6 0 1112 0A6 6 0 014 10z" clip-rule="evenodd"/>
              </svg>
              <svg v-else class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
              <span class="text-sm font-medium text-gray-900">
                {{ isOnline ? 'Online' : 'Offline Mode' }}
              </span>
            </div>
            
            <div v-if="offlineQueueStats.total > 0" class="flex items-center space-x-4 text-sm text-gray-600">
              <span class="flex items-center space-x-1">
                <span class="w-2 h-2 bg-yellow-400 rounded-full"></span>
                <span>{{ offlineQueueStats.pending }} pending</span>
              </span>
              <span v-if="offlineQueueStats.failed > 0" class="flex items-center space-x-1">
                <span class="w-2 h-2 bg-red-400 rounded-full"></span>
                <span>{{ offlineQueueStats.failed }} failed</span>
              </span>
              <span v-if="offlineQueueStats.synced > 0" class="flex items-center space-x-1">
                <span class="w-2 h-2 bg-green-400 rounded-full"></span>
                <span>{{ offlineQueueStats.synced }} synced</span>
              </span>
            </div>
          </div>
          
          <div class="flex items-center space-x-2">
            <button 
              v-if="offlineQueueStats.pending > 0 && isOnline"
              @click="syncOfflineTransactions"
              :disabled="syncInProgress"
              class="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <div v-if="syncInProgress" class="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
              <svg v-else class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
              </svg>
              <span>{{ syncInProgress ? 'Syncing...' : 'Sync' }}</span>
            </button>
            
            <button 
              v-if="offlineQueueStats.failed > 0"
              @click="retryFailedTransactions"
              class="flex items-center space-x-1 px-2 py-1 text-xs bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
            >
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
              </svg>
              <span>Retry</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Modals -->
    <!-- Customer Selection Modal -->
    <div v-if="showCustomerModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 touch-manipulation" @click.self="closeCustomerModal">
      <div class="bg-white rounded-lg w-full max-w-md lg:max-w-4xl modal-scroll overflow-hidden shadow-xl">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                {{ showAddCustomerForm ? 'Add New Customer' : 'Select Customer' }}
              </h3>
              <button 
                v-if="!showAddCustomerForm"
                @click="toggleAddCustomerForm"
                class="inline-flex items-center px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
                Add New
              </button>
            </div>
            
            <!-- Add New Customer Form -->
            <div v-if="showAddCustomerForm" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  v-model="newCustomer.name"
                  type="text"
                  placeholder="Customer name"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                <div class="flex items-center space-x-2">
                  <input
                    v-model="newCustomer.mobile_no"
                    type="tel"
                    placeholder="10-digit mobile number"
                    maxlength="10"
                    pattern="[0-9]{10}"
                    @input="validateMobileNumber"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    :class="{ 'border-red-500': mobileNumberError }"
                  >
                  <!-- Contact Picker Button -->
                  <button
                    v-if="isContactPickerAvailable"
                    @click="selectContactForNewCustomer"
                    :disabled="isSelectingContact"
                    type="button"
                    class="flex items-center justify-center p-2 bg-green-600 text-white rounded-md hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors touch-manipulation"
                    :title="isSelectingContact ? 'Selecting contact...' : 'Select from contacts'"
                  >
                    <svg v-if="!isSelectingContact" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                    </svg>
                    <svg v-else class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </button>
                  <!-- Fallback info for unsupported devices -->
                  <div v-else class="flex items-center justify-center p-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed" title="Contact picker not supported on this device">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                </div>
                <p v-if="mobileNumberError" class="text-red-500 text-xs mt-1">{{ mobileNumberError }}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  v-model="newCustomer.email"
                  type="email"
                  placeholder="Email address"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
              </div>
              
              <!-- Customer Type Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Customer Type *</label>
                <div class="flex space-x-4">
                  <label class="flex items-center">
                    <input
                      v-model="newCustomer.customer_type"
                      type="radio"
                      value="B2C"
                      @change="onCustomerTypeChange"
                      class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    >
                    <span class="ml-2 text-sm text-gray-700">Individual (B2C)</span>
                  </label>
                  <label class="flex items-center">
                    <input
                      v-model="newCustomer.customer_type"
                      type="radio"
                      value="B2B"
                      @change="onCustomerTypeChange"
                      class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    >
                    <span class="ml-2 text-sm text-gray-700">Business (B2B)</span>
                  </label>
                </div>
              </div>
              
              <!-- GSTIN Field (only for B2B customers) -->
              <div v-if="newCustomer.customer_type === 'B2B'">
                <label class="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                <input
                  v-model="newCustomer.gstin"
                  type="text"
                  placeholder="22AAAAA0000A1Z5"
                  maxlength="15"
                  @input="validateGSTIN"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  :class="{ 'border-red-500': gstinError }"
                >
                <p v-if="gstinError" class="text-red-500 text-xs mt-1">{{ gstinError }}</p>
                <p class="text-gray-500 text-xs mt-1">Leave empty for unregistered business customers</p>
              </div>
              
              <!-- Allow Credit Sale -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Credit Sales *</label>
                <div class="flex items-center space-x-4">
                  <label class="flex items-center">
                    <input
                      v-model="newCustomer.allow_credit"
                      type="radio"
                      :value="true"
                      class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    >
                    <span class="ml-2 text-sm text-gray-700">Allow Credit Sales</span>
                  </label>
                  <label class="flex items-center">
                    <input
                      v-model="newCustomer.allow_credit"
                      type="radio"
                      :value="false"
                      class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    >
                    <span class="ml-2 text-sm text-gray-700">Cash Only</span>
                  </label>
                </div>
              </div>
              
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Street</label>
                  <input
                    v-model="newCustomer.address.street"
                    type="text"
                    placeholder="Street address"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    v-model="newCustomer.address.city"
                    type="text"
                    placeholder="City"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                </div>
              </div>
              
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <select
                    v-model="newCustomer.address.state"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select State</option>
                    <option v-for="state in indianStates" :key="state.code" :value="state.name">
                      {{ state.name }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    v-model="newCustomer.address.pincode"
                    type="text"
                    placeholder="Pincode"
                    maxlength="6"
                    pattern="[0-9]{6}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                </div>
              </div>
            </div>
            
            <!-- Customer Selection -->
            <div v-else>
              <div class="mb-4">
                <input
                  v-model="customerSearch"
                  @input="searchCustomers"
                  type="text"
                  placeholder="Search customers..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
              </div>
              
              <div class="max-h-80 overflow-y-auto">
                <!-- Walk-in Customer Option -->
                <div 
                  @click="selectCustomer(null)"
                  class="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                >
                  <div class="font-medium text-gray-900">Walk-in Customer</div>
                  <div class="text-sm text-gray-500">No customer details required</div>
                </div>
                
                <!-- Customer List -->
                <div 
                  v-for="customer in filteredCustomers" 
                  :key="customer._id"
                  @click="selectCustomer(customer)"
                  class="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <div class="font-medium text-gray-900">{{ customer.name }}</div>
                      <div class="text-sm text-gray-500">{{ customer.mobile_no }}</div>
                    </div>
                    <div class="text-right">
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            :class="customer.customer_type === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'">
                        {{ customer.customer_type }}
                      </span>
                      <div v-if="!customer.allow_credit" class="text-xs text-red-600 mt-1">Cash Only</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <!-- Add Customer Form Buttons -->
            <template v-if="showAddCustomerForm">
              <button 
                @click="saveNewCustomer"
                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Save Customer
              </button>
              <button 
                @click="toggleAddCustomerForm"
                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Back to List
              </button>
            </template>
            
            <!-- Customer Selection Buttons -->
            <template v-else>
              <button 
                @click="closeCustomerModal"
                class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </template>
          </div>
        </div>
    </div>

    <!-- Products Modal -->
    <div v-if="showProductsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4 touch-manipulation" @click.self="showProductsModal = false">
      <div class="bg-white rounded-lg w-full max-w-md lg:max-w-4xl modal-scroll overflow-hidden shadow-xl">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900">Select Product</h3>
              <button @click="showProductsModal = false" class="text-gray-400 hover:text-gray-600">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
            
            <!-- Category Tabs -->
            <div class="mb-4">
              <div class="flex space-x-2 overflow-x-auto pb-2">
                <button 
                  v-for="category in categories" 
                  :key="category"
                  @click="selectedCategory = category"
                  class="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors"
                  :class="selectedCategory === category 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'"
                >
                  {{ category }}
                </button>
              </div>
            </div>
            
            <!-- Products Grid -->
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[70vh] overflow-y-auto">
              <div 
                v-for="product in filteredProducts" 
                :key="product._id"
                @click="(!allowOverselling && (product.stock_quantity || 0) <= 0) ? null : addToCart(product)"
                :class="{
                  'opacity-50 cursor-not-allowed': !allowOverselling && (product.stock_quantity || 0) <= 0,
                  'cursor-pointer hover:shadow-lg hover:bg-blue-50 active:bg-blue-100 hover:scale-105 active:scale-95': allowOverselling || (product.stock_quantity || 0) > 0
                }"
                class="border border-gray-200 rounded-lg p-4 transition-all duration-200 transform"
              >
                <div class="text-sm font-medium text-gray-900 mb-1">{{ product.name }}</div>
                <div class="text-xs text-gray-500 mb-2">{{ product.short_code }}</div>
                <div class="flex justify-between items-center">
                  <span class="text-lg font-semibold text-green-600">₹{{ formatPrice(product.pricing?.sale_price_inc_tax || 0) }}</span>
                  <span class="text-xs" :class="!allowOverselling && (product.stock_quantity || 0) <= 0 ? 'text-red-500 font-medium' : 'text-gray-500'">
                    Stock: {{ product.stock_quantity || 0 }}
                    <span v-if="!allowOverselling && (product.stock_quantity || 0) <= 0" class="block text-red-500">Out of Stock</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>

    <!-- Barcode Scanner Modal -->
    <div v-if="showScannerModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 touch-manipulation" @click.self="stopBarcodeScanner">
      <div class="bg-white rounded-lg w-full max-w-md lg:max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">Scan Barcode</h3>
          <button @click="stopBarcodeScanner" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-4">
          <div id="barcode-scanner" class="w-full"></div>
          <p class="text-sm text-gray-600 mt-4 text-center">Position barcode within the frame</p>
        </div>
      </div>
    </div>

    <!-- Price Edit Modal -->
    <div v-if="editingPrice" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 touch-manipulation" @click.self="cancelEdit">
      <div class="bg-white rounded-lg w-full max-w-md lg:max-w-lg modal-scroll overflow-hidden shadow-xl">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">Edit Price</h3>
            <p class="text-sm text-gray-600 mb-4">{{ editingPrice?.name }}</p>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Price (₹)</label>
              <input
                v-model.number="newPrice"
                type="number"
                min="0"
                step="0.01"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
            </div>
          </div>
          
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              @click="savePrice"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Save
            </button>
            <button 
              @click="cancelEdit"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
    </div>

    <!-- Tax Edit Modal -->
    <div v-if="editingTax" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 touch-manipulation" @click.self="cancelEdit">
      <div class="bg-white rounded-lg w-full max-w-md lg:max-w-lg modal-scroll overflow-hidden shadow-xl">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">Edit Tax Rate</h3>
            <p class="text-sm text-gray-600 mb-4">{{ editingTax?.name }}</p>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
              <input
                v-model.number="newTaxRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
            </div>
          </div>
          
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              @click="saveTax"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Save
            </button>
            <button 
              @click="cancelEdit"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
    </div>
  </div>

  <!-- GST Price Editor Modal -->
  <div 
    v-if="showPriceModal" 
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    @click.self="closePriceModal"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md">
      <div class="p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Edit Price - {{ selectedItem?.name }}</h3>
        
        <!-- Tax Rate Selection -->
        <div v-if="isGstEnabled" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">GST Rate</label>
          <select 
            v-model="tempTaxRate"
            @change="calculatePrices"
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option v-for="option in taxRateOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        
        <!-- Price Without GST -->
        <div v-if="isGstEnabled" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Price (Without GST)</label>
          <input 
            v-model.number="tempPriceWithoutGst"
            @input="calculateFromExclusivePrice"
            @focus="openPriceKeypadForModal('exclusive')"
            type="number"
            :step="Math.pow(10, -decimalPrecision)"
            readonly
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            placeholder="Enter price without GST"
          >
        </div>
        
        <!-- Price With GST -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ isGstEnabled ? 'Price (With GST)' : 'Price' }}</label>
          <input 
            v-model.number="tempPriceWithGst"
            @input="calculateFromInclusivePrice"
            @focus="openPriceKeypadForModal('inclusive')"
            type="number"
            :step="Math.pow(10, -decimalPrecision)"
            readonly
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            :placeholder="isGstEnabled ? 'Enter price with GST' : 'Enter price'"
          >
        </div>
        
        <!-- Tax Amount Display -->
        <div v-if="isGstEnabled" class="mb-6 p-3 bg-yellow-50 rounded-md">
          <p class="text-sm text-gray-700">
            <span class="font-medium">Tax Amount:</span> ₹{{ formatPrice(tempTaxAmount) }}
          </p>
        </div>
        
        <!-- Action Buttons -->
        <div class="flex space-x-3">
          <button 
            @click="closePriceModal"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="savePriceChanges"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
    </div>

    <!-- Numeric Keypad Modal -->
    <NumericKeypad
      :visible="keypadVisible"
      :title="keypadConfig.title"
      :subtitle="keypadConfig.subtitle"
      :mode="keypadConfig.mode"
      :initial-value="keypadConfig.initialValue"
      :confirm-text="keypadConfig.confirmText"
      :max-value="keypadConfig.maxValue"
      :min-value="keypadConfig.minValue"
      :max-digits="keypadConfig.maxDigits"
      :show-quick-amounts="keypadConfig.showQuickAmounts"
      @confirm="handleKeypadConfirm"
      @cancel="handleKeypadCancel"
      @close="handleKeypadCancel"
    />

    <!-- UPI Payment Modal -->
    <UpiPaymentModal 
      :key="`upi-modal-${showUpiModal}`"
      :show="showUpiModal"
      :total-amount="total"
      :business-v-p-a="businessVPA"
      :merchant-name="authStore.business?.name || ''"
      :transaction-note="`Payment - ${generateTransactionRef()}`"
      :transaction-ref="pendingTransactionRef"
      @close="closeUpiModal"
      @payment-received="handleUpiPaymentReceived"
      @change-payment-method="handleChangePaymentMethod"
    />

    <!-- WhatsApp Share Modal -->
    <WhatsAppShareModal
      :visible="showWhatsAppModal"
      :transaction="completedTransaction"
      :business="authStore.business"
      @close="closeWhatsAppModal"
      @skip="handleWhatsAppSkip"
      @sent="handleWhatsAppSent"
      @dont-show-again="handleWhatsAppDontShowAgain"
    />

    <!-- Custom Toast Notifications -->
    <div class="custom-toast-container">
      <div
        v-for="toast in toastMessages"
        :key="toast.id"
        :class="['custom-toast', `custom-toast-${toast.type}`]"
      >
        <div class="custom-toast-icon">
          <!-- Success Icon -->
          <svg v-if="toast.type === 'success'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <!-- Error Icon -->
          <svg v-else-if="toast.type === 'error'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          <!-- Warning Icon -->
          <svg v-else-if="toast.type === 'warning'" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <!-- Info Icon -->
          <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
          </svg>
        </div>
        <span class="custom-toast-message">{{ toast.message }}</span>
      </div>
    </div>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBranding } from '@/composables/useBranding'
import { productsApi } from '@/services/products'
import { customersApi } from '@/services/customers'
import { transactionsApi } from '@/services/transactions'
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats, Html5QrcodeScanType } from 'html5-qrcode'
import { indianStates, isInterStateTransaction, calculateItemGST, calculateGSTSummary, extractGSTFromInclusiveAmount } from '@/utils/gstUtils'
import { isEligibleForWhatsAppShare } from '@/utils/whatsappUtils'
import { isContactPickerSupported, selectContacts } from '@/utils/contactPicker'
import NumericKeypad from '@/components/common/NumericKeypad.vue'
import UpiPaymentModal from '@/components/common/UpiPaymentModal.vue'
import WhatsAppShareModal from '@/components/common/WhatsAppShareModal.vue'
import { useNumericKeypad } from '@/composables/useNumericKeypad'
import { useSounds } from '@/composables/useSounds'
import offlineQueue from '@/services/offlineQueue'
import { ReceiptTemplateEngine } from '@/utils/receiptTemplates'
import { usePrinter } from '@/composables/usePrinter'


const router = useRouter()
const authStore = useAuthStore()
const branding = useBranding()

// Custom toast system for POS view
const toastMessages = ref([])
let toastIdCounter = 0

const toast = {
  success: (message) => showToast(message, 'success'),
  error: (message) => showToast(message, 'error'),
  warning: (message) => showToast(message, 'warning'),
  info: (message) => showToast(message, 'info')
}

const showToast = (message, type) => {
  const id = ++toastIdCounter
  toastMessages.value.push({ id, message, type })
  
  // Auto remove after timeout
  const timeout = type === 'error' || type === 'warning' ? 2500 : 2000
  setTimeout(() => {
    removeToast(id)
  }, timeout)
}

const removeToast = (id) => {
  const index = toastMessages.value.findIndex(t => t.id === id)
  if (index > -1) {
    toastMessages.value.splice(index, 1)
  }
}
const { 
  playTransactionComplete, 
  playItemAdded, 
  playItemRemoved, 
  playErrorNotification,
  playBarcodeScan,
  playButtonClick
} = useSounds()

// Printer Status Management
// Initialize printer composable
const {
  isConnected: printerConnected,
  isConnecting: printerConnecting,
  isPrinting: printerPrinting,
  connectedDevice,
  discoverPrinters,
  connectToPrinter,
  disconnectPrinter,
  printReceiptWithTemplate,
  testPrint,
  autoConnect
} = usePrinter()

// Streamlined printer status (matching Transactions view)
const printerStatus = computed(() => ({
  isConnected: printerConnected.value,
  deviceName: connectedDevice.value?.name || '',
  device: connectedDevice.value,
  characteristic: connectedDevice.value?.characteristic || null,
  isPrinting: printerPrinting.value
}))

// Auto Print Settings
const autoprint = ref(false)

// Local app settings (for compatibility) - now syncs with global settings
const appSettings = ref({
  autoPrint: false,
  printMethod: 'web',
  webPrintSettings: {
    receiptSize: 'thermal',
    openDialog: true
  }
})

// Reactive variables
const products = ref([])
const customers = ref([])
const cartItems = ref([])
const selectedCustomer = ref(null)
const searchQuery = ref('')
const searchResults = ref([])
const selectedSearchIndex = ref(0)
const lastProductLoadTime = ref(0) // Track last product load time
const customerSearch = ref('')
const filteredCustomers = ref([])
const categories = ref(['All'])
const selectedCategory = ref('All')
const loading = ref(false)

// Offline sync status
const isOnline = ref(navigator.onLine)
const offlineQueueStats = ref({ total: 0, pending: 0, failed: 0 })
const syncInProgress = ref(false)

// Numeric Keypad Composable
const {
  isVisible: keypadVisible,
  keypadConfig,
  handleConfirm: handleKeypadConfirm,
  handleCancel: handleKeypadCancel,
  openKeypad,
  openQuantityKeypad,
  openPriceKeypad,
  openPaymentKeypad
} = useNumericKeypad()

// Modal states
const showCustomerModal = ref(false)
const showProductsModal = ref(false)
const showScannerModal = ref(false)
const showPriceModal = ref(false)
const editingPrice = ref(null)
const editingTax = ref(null)
const showAddCustomerForm = ref(false)
const showUpiModal = ref(false)
const showWhatsAppModal = ref(false)

// UPI Payment states
const pendingTransactionData = ref(null)
const pendingTransactionRef = ref('')

// WhatsApp sharing states
const completedTransaction = ref(null)

// Price modal variables
const selectedItem = ref(null)
const tempTaxRate = ref(0)
const tempPriceWithoutGst = ref(0)
const tempPriceWithGst = ref(0)
const tempTaxAmount = ref(0)

// Form validation
const mobileNumberError = ref('')
const gstinError = ref('')

// Contact picker functionality
const isContactPickerAvailable = ref(false)
const isSelectingContact = ref(false)

// Edit values
const newPrice = ref(0)
const newTaxRate = ref(0)
const priceWithoutGst = ref(0)
const priceWithGst = ref(0)
const selectedTaxRate = ref(0)

// Discount
const discount = ref(0)
const discountType = ref('percentage') // 'percentage' or 'amount'

// New customer form
const newCustomer = ref({
  name: '',
  mobile_no: '',
  email: '',
  gstin: '',
  customer_type: 'B2C',
  allow_credit: true,
  address: {
    street: '',
    city: '',
    state: '',
    pincode: ''
  }
})

// Scanner
let scanner = null

// GST settings
const isGstEnabled = computed(() => {
  const gstEnabled = authStore.business?.gst_enabled || false

  //   businessExists: !!authStore.business,
  //   gstEnabled: gstEnabled,
  //   businessData: authStore.business
  // })
  return gstEnabled
})
const defaultGstRate = computed(() => authStore.business?.gst_settings?.default_gst_rate || 18)

// Decimal precision for price formatting
const decimalPrecision = computed(() => authStore.business?.decimal_precision || 2)

// Allow overselling setting
const allowOverselling = computed(() => authStore.business?.allow_overselling || false)

// Business VPA for UPI payments
const businessVPA = computed(() => authStore.business?.upi_vpa || '')

// Utility function to round amounts to configured decimal places
const roundAmount = (amount) => {
  return Math.round(amount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
}

// Utility function to format prices for display
const formatPrice = (amount) => {
  if (isNaN(amount) || amount === null || amount === undefined) {
    return '0'
  }
  return roundAmount(amount).toFixed(decimalPrecision.value)
}

// Tax rate options for dropdown
const taxRateOptions = computed(() => {
  const rates = [0, 5, 12, 18, 28] // Common GST rates in India
  return rates.map(rate => ({ value: rate, label: `${rate}%` }))
})

// Check if transaction is inter-state (for IGST calculation)
const isInterState = computed(() => {
  if (!isGstEnabled.value || !selectedCustomer.value || !authStore.business) {
    return false
  }
  
  const businessState = authStore.business.address?.state
  const customerState = selectedCustomer.value.address?.state
  
  return isInterStateTransaction(businessState, customerState)
})

// Computed properties
const filteredProducts = computed(() => {
  if (selectedCategory.value === 'All') {
    return products.value
  }
  return products.value.filter(product => product.category === selectedCategory.value)
})

const subtotal = computed(() => {
  if (!isGstEnabled.value) {
    // When GST is disabled, use the full price as subtotal
    return roundAmount(cartItems.value.reduce((sum, item) => {
      return sum + (item.price * item.quantity)
    }, 0))
  }
  
  // When GST is enabled and prices are GST-inclusive, subtotal should be the base amount (excluding GST)
  return roundAmount(cartItems.value.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity
    const gstDetails = extractGSTFromInclusiveAmount(itemTotal, item.tax_rate, isInterState.value)
    return sum + gstDetails.baseAmount
  }, 0))
})

const discountAmount = computed(() => {
  if (discountType.value === 'percentage') {
    return roundAmount((subtotal.value * discount.value) / 100)
  }
  return roundAmount(discount.value || 0)
})

const gstAmount = computed(() => {
  if (!isGstEnabled.value) {
    return 0
  }
  
  return roundAmount(cartItems.value.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity
    // Since prices are GST-inclusive, extract GST instead of adding it
    const gstDetails = extractGSTFromInclusiveAmount(itemTotal, item.tax_rate, isInterState.value)
    return sum + gstDetails.totalGst
  }, 0))
})

// Calculate GST breakdown for display
const gstBreakdown = computed(() => {
  if (!isGstEnabled.value) {
    return { cgst: 0, sgst: 0, igst: 0, total: 0 }
  }
  
  const breakdown = { cgst: 0, sgst: 0, igst: 0, total: 0 }
  
  cartItems.value.forEach(item => {
    const itemTotal = item.price * item.quantity
    // Since prices are GST-inclusive, extract GST instead of adding it
    const gstDetails = extractGSTFromInclusiveAmount(itemTotal, item.tax_rate, isInterState.value)
    
    breakdown.cgst += gstDetails.cgstAmount
    breakdown.sgst += gstDetails.sgstAmount
    breakdown.igst += gstDetails.igstAmount
    breakdown.total += gstDetails.totalGst
  })
  
  // Round to configured decimal places
  breakdown.cgst = roundAmount(breakdown.cgst)
  breakdown.sgst = roundAmount(breakdown.sgst)
  breakdown.igst = roundAmount(breakdown.igst)
  breakdown.total = roundAmount(breakdown.total)
  
  return breakdown
})

const total = computed(() => {
  return roundAmount(subtotal.value - discountAmount.value + gstAmount.value)
})

// Methods
const loadProducts = async (force = false) => {
  try {
    // Throttle: Don't reload if loaded in last 2 seconds (unless forced)
    const now = Date.now()
    if (!force && (now - lastProductLoadTime.value) < 2000) {
      console.log('⏭️ Skipping product reload - too soon')
      return
    }
    
    loading.value = true
    console.log('🔄 Reloading products to refresh stock quantities...')
    
    // Check if authenticated before making API call
    if (!authStore.isAuthenticated) {
      console.log('⚠️ Not authenticated, skipping product reload')
      return
    }

    // Add timestamp to bust cache
    const response = await productsApi.getProducts({ 
      limit: 10000,
      _t: Date.now() // Cache buster
    })
    console.log('✅ Products reloaded, total:', response.products?.length)
    
    lastProductLoadTime.value = now
    
    // Ensure we have a valid response
    if (response && response.products) {
      products.value = response.products || []
      
      // Extract unique categories safely
      const productCategories = [...new Set(products.value.map(p => p.category).filter(Boolean))]
      categories.value = ['All', ...productCategories]
    } else {
      console.warn('Invalid products response:', response)
      products.value = []
      categories.value = ['All']
    }
  } catch (error) {
    console.error('Error loading products:', error)
    if (error.status === 401) {
      toast.error('Authentication required. Please login again.')
      router.push('/login')
    } else {
      toast.error('Failed to load products')
    }
  } finally {
    loading.value = false
  }
}

const loadCustomers = async () => {
  try {
    // Check if authenticated before making API call
    if (!authStore.isAuthenticated) {

      return
    }

    // console.log('Loading customers...')
    const response = await customersApi.getCustomers({ limit: 10000 })
    // console.log('Customers API response:', response)
    
    // Defensive coding - validate response structure
    if (response && typeof response === 'object' && Array.isArray(response.customers)) {
      customers.value = response.customers || []
      filteredCustomers.value = customers.value
      // console.log('Loaded customers:', customers.value.length)
    } else {
      console.warn('Invalid customers response format:', response)
      customers.value = []
      filteredCustomers.value = []
    }
  } catch (error) {
    console.error('Error loading customers:', error)
    customers.value = []
    filteredCustomers.value = []
    
    if (error && error.status === 401) {
      toast.error('Authentication required. Please login again.')
      router.push('/login')
    } else {
      toast.error('Failed to load customers')
    }
  }
}

const loadBusinessInfo = async () => {
  try {
    if (!authStore.isAuthenticated) {
      // console.log('Not authenticated, skipping business load')
      return
    }

    // console.log('Loading business information...')
    // console.log('Business ID:', authStore.businessId)
    // console.log('Current business:', authStore.business)
    
    // If business already has GST info, skip loading
    if (authStore.business?.gst_enabled !== undefined) {
      // console.log('Business already has GST info, skipping reload')
      return
    }
    
    await authStore.loadBusinessContext()
    // console.log('Business info updated via auth store:', authStore.business)
  } catch (error) {
    console.error('Error loading business info:', error)
    // Don't show error toast as this is background loading, just log it
  }
}

const searchProducts = () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }
  
  const query = searchQuery.value.toLowerCase()
  searchResults.value = products.value.filter(product => 
    product.name.toLowerCase().includes(query) ||
    product.short_code?.toLowerCase().includes(query) ||
    product.barcode?.toLowerCase().includes(query)
  ).slice(0, 100)
  
  selectedSearchIndex.value = 0
}

const searchCustomers = () => {
  if (!customerSearch.value.trim()) {
    filteredCustomers.value = customers.value
    return
  }
  
  const query = customerSearch.value.toLowerCase()
  filteredCustomers.value = customers.value.filter(customer =>
    customer.name.toLowerCase().includes(query) ||
    customer.mobile_no?.toLowerCase().includes(query) ||
    customer.email?.toLowerCase().includes(query)
  )
}

const selectFirstProduct = () => {
  if (searchResults.value.length > 0) {
    addToCart(searchResults.value[selectedSearchIndex.value])
    clearSearch()
  }
}

const navigateSearchResults = (direction) => {
  if (searchResults.value.length === 0) return
  
  selectedSearchIndex.value += direction
  if (selectedSearchIndex.value < 0) {
    selectedSearchIndex.value = searchResults.value.length - 1
  } else if (selectedSearchIndex.value >= searchResults.value.length) {
    selectedSearchIndex.value = 0
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = []
  selectedSearchIndex.value = 0
}

const addToCart = (product) => {
  // console.log('Adding product to cart:', product.name, 'MRP:', product.pricing?.mrp, 'Sale Price:', product.pricing?.sale_price_inc_tax)
  
  // Check stock availability if overselling is not allowed
  if (!allowOverselling.value && (product.stock_quantity || 0) <= 0) {
    toast.warning(`${product.name} is out of stock`)
    return
  }
  
  const existingItem = cartItems.value.find(item => item.id === product._id)
  
  if (existingItem) {
    // Always increment by 1 for product clicks, regardless of decimal settings
    const incrementValue = 1
    
    const newQuantity = existingItem.quantity + incrementValue
    
    // Check if we can add more to existing item
    if (!allowOverselling.value && newQuantity > existingItem.stock) {
      toast.warning(`Only ${existingItem.stock} ${product.unit || 'pieces'} available in stock`)
      return
    }
    
    existingItem.quantity = newQuantity
  } else {
    // Always start with quantity 1 for product clicks, regardless of decimal settings
    const defaultQuantity = 1
    
    const inclusivePrice = product.pricing?.sale_price_inc_tax || 0
    const taxRate = isGstEnabled.value ? (product.gst_rate?.total || authStore.business?.gst_settings?.default_gst_rate || 0) : 0
    
    // Calculate correct GST prices
    let exclusivePrice = inclusivePrice
    if (isGstEnabled.value && taxRate > 0) {
      // Calculate exclusive price from inclusive price
      exclusivePrice = Math.round((inclusivePrice / (1 + taxRate / 100)) * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
    }

    const cartItem = {
      id: product._id,
      name: product.name,
      short_code: product.short_code,
      barcode: product.barcode || product.short_code,
      price: inclusivePrice,
      mrp: product.pricing?.mrp || 0, // Add MRP from product pricing
      priceWithoutGst: exclusivePrice,
      priceWithGst: inclusivePrice,
      tax_rate: taxRate,
      quantity: defaultQuantity,
      stock: product.stock_quantity || 0,
      // Add decimal support fields
      unit: product.unit || 'piece',
      decimal_allowed: product.decimal_allowed || false,
      decimal_precision: authStore.business?.quantity_decimal_precision || 2 // Use business setting
    }
    
    cartItems.value.push(cartItem)
  }
  
  // Play item added sound
  playItemAdded()
  
  // Show toast notification for user feedback, especially important when modal is open
  if (existingItem) {
    toast.success(`${product.name} quantity increased to ${existingItem.quantity}`)
  } else {
    toast.success(`${product.name} added to cart`)
  }
}

const updateQuantity = (itemId, newQuantity) => {
  const item = cartItems.value.find(item => item.id === itemId)
  if (!item) return
  
  // Calculate minimum allowed quantity based on precision
  const precision = item.decimal_allowed ? (item.decimal_precision || 2) : 0
  const minQuantity = precision > 0 ? 1 / Math.pow(10, precision) : 1
  
  if (newQuantity < minQuantity) {
    removeFromCart(itemId)
    return
  }
  
  // Round to appropriate decimal places based on product settings
  const roundedQuantity = parseFloat(newQuantity.toFixed(precision))
  
  // Check stock only if overselling is not allowed
  if (!allowOverselling.value && roundedQuantity > item.stock) {
    toast.warning(`Only ${item.stock} ${item.unit || 'pieces'} available in stock`)
    return
  }
  
  item.quantity = roundedQuantity
}

// Helper functions for decimal quantity handling
const getStepValue = (precision = null) => {
  // Use provided precision or fall back to business setting
  const actualPrecision = precision !== null ? precision : (authStore.business?.quantity_decimal_precision || 2)
  
  switch(actualPrecision) {
    case 3: return '0.001'
    case 2: return '0.01'  
    case 1: return '0.1'
    case 0: return '1'
    default: return '0.01'
  }
}

const getIncrementValue = (item) => {
  if (!item.decimal_allowed) return item.quantity + 1
  
  const step = parseFloat(getStepValue()) // Use business setting
  return item.quantity + step
}

const getDecrementValue = (item) => {
  if (!item.decimal_allowed) return item.quantity - 1
  
  const step = parseFloat(getStepValue()) // Use business setting
  const newValue = item.quantity - step
  // Allow decimal quantities down to the step precision
  return Math.max(step, newValue)
}

// Format quantity for display
const formatQuantity = (quantity, decimalAllowed, precision = null) => {
  // Use business setting if no precision specified
  const actualPrecision = precision !== null ? precision : (authStore.business?.quantity_decimal_precision || 2)
  
  if (!decimalAllowed || quantity % 1 === 0) {
    return quantity.toString()
  }
  return quantity.toFixed(actualPrecision).replace(/\.?0+$/, '')
}

const removeFromCart = (itemId) => {
  const index = cartItems.value.findIndex(item => item.id === itemId)
  if (index > -1) {
    cartItems.value.splice(index, 1)
    
    // Play item removed sound
    playItemRemoved()
    
    // Removed toast - visual cart update is sufficient
    // toast.info('Item removed from cart')
  }
}

const updateItemPrice = (item) => {
  // Ensure price is valid
  if (item.price <= 0) {
    item.price = item.selling_price || 0
    toast.warning('Price must be greater than 0')
  }
}

// Numeric Keypad Methods
const openQuantityKeypadForItem = (item) => {
  // Check if product allows decimal quantities
  // Try both id and _id fields since MongoDB uses _id
  const product = products.value.find(p => p._id === item.id || p.id === item.id || p._id === item.product_id)
  const allowsDecimal = product?.decimal_allowed || false
  // Use business setting for precision instead of product-specific
  const precision = authStore.business?.quantity_decimal_precision || 2
  
  if (allowsDecimal) {
    // Use custom keypad config for decimal quantities
    const config = {
      title: 'Enter Quantity',
      subtitle: `How many ${product.unit || 'items'}?`,
      mode: 'decimal',
      initialValue: item.quantity.toString(),
      confirmText: 'Update Quantity',
      maxValue: 9999,
      minValue: parseFloat(getStepValue(precision)), // Use business setting for precision
      maxDigits: 6, // Allow more digits for decimal values
      showQuickAmounts: false,
      onConfirm: (newQuantity) => {
        // Round to the business-specified precision
        const roundedQuantity = Math.round(parseFloat(newQuantity) * Math.pow(10, precision)) / Math.pow(10, precision)
        updateQuantity(item.id, roundedQuantity)
      }
    }
    
    openKeypad(config)
  } else {
    // Use standard integer quantity keypad
    openQuantityKeypad(item.quantity.toString(), (newQuantity) => {
      updateQuantity(item.id, newQuantity)
    })
  }
}

const openPriceKeypadForItem = (item) => {
  openPriceKeypad(item.price.toString(), (newPrice) => {
    item.price = newPrice
    updateItemPrice(item)
  })
}

const openPriceKeypadForModal = (type) => {
  const initialValue = type === 'inclusive' ? tempPriceWithGst.value : tempPriceWithoutGst.value
  const title = type === 'inclusive' ? 'Enter Price (With GST)' : 'Enter Price (Without GST)'
  
  openPriceKeypad(initialValue?.toString() || '', (newPrice) => {
    if (type === 'inclusive') {
      tempPriceWithGst.value = newPrice
      calculateFromInclusivePrice()
    } else {
      tempPriceWithoutGst.value = newPrice
      calculateFromExclusivePrice()
    }
  })
}

const updateItemTax = (item) => {
  // If GST is disabled, always set tax rate to 0
  if (!isGstEnabled.value) {
    item.tax_rate = 0
    return
  }
  
  // Ensure tax rate is valid
  if (item.tax_rate < 0) {
    item.tax_rate = 0
  } else if (item.tax_rate > 100) {
    item.tax_rate = 100
  }
}

const openPriceModal = (item) => {
  selectedItem.value = item
  tempTaxRate.value = item.tax_rate || defaultGstRate.value
  
  // Always recalculate GST prices to ensure accuracy
  tempPriceWithGst.value = parseFloat(item.price)
  
  if (tempTaxRate.value > 0) {
    // Calculate exclusive price from inclusive price with high precision
    const preciseExclusivePrice = item.price / (1 + tempTaxRate.value / 100)
    tempPriceWithoutGst.value = Math.round(preciseExclusivePrice * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  } else {
    tempPriceWithoutGst.value = parseFloat(item.price)
  }
  
  // Calculate tax amount with precision
  tempTaxAmount.value = Math.round((tempPriceWithGst.value - tempPriceWithoutGst.value) * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  
  showPriceModal.value = true
}

const closePriceModal = () => {
  showPriceModal.value = false
  selectedItem.value = null
  tempTaxRate.value = 0
  tempPriceWithoutGst.value = 0
  tempPriceWithGst.value = 0
  tempTaxAmount.value = 0
}

const calculateFromExclusivePrice = () => {
  if (tempPriceWithoutGst.value > 0 && tempTaxRate.value >= 0) {
    // Calculate tax amount with proper rounding
    const taxAmount = tempPriceWithoutGst.value * (tempTaxRate.value / 100)
    tempTaxAmount.value = Math.round(taxAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
    
    // Calculate inclusive price
    const totalAmount = tempPriceWithoutGst.value + tempTaxAmount.value
    tempPriceWithGst.value = Math.round(totalAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  }
}

const calculateFromInclusivePrice = () => {
  if (tempPriceWithGst.value > 0 && tempTaxRate.value >= 0) {
    // Calculate exclusive price with proper rounding
    const exclusivePrice = tempPriceWithGst.value / (1 + tempTaxRate.value / 100)
    tempPriceWithoutGst.value = Math.round(exclusivePrice * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
    
    // Calculate tax amount
    const taxAmount = tempPriceWithGst.value - tempPriceWithoutGst.value
    tempTaxAmount.value = Math.round(taxAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  }
}

const calculatePrices = () => {
  // Calculate tax amount based on current values with proper precision
  if (tempPriceWithoutGst.value > 0 && tempTaxRate.value >= 0) {
    // Calculate from exclusive price
    const taxAmount = tempPriceWithoutGst.value * (tempTaxRate.value / 100)
    tempTaxAmount.value = Math.round(taxAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
    
    const totalAmount = tempPriceWithoutGst.value + tempTaxAmount.value
    tempPriceWithGst.value = Math.round(totalAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  } else if (tempPriceWithGst.value > 0 && tempTaxRate.value >= 0) {
    // Calculate from inclusive price
    const exclusivePrice = tempPriceWithGst.value / (1 + tempTaxRate.value / 100)
    tempPriceWithoutGst.value = Math.round(exclusivePrice * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
    
    const taxAmount = tempPriceWithGst.value - tempPriceWithoutGst.value
    tempTaxAmount.value = Math.round(taxAmount * Math.pow(10, decimalPrecision.value)) / Math.pow(10, decimalPrecision.value)
  }
}

const savePriceChanges = () => {
  if (selectedItem.value) {
    const itemIndex = cartItems.value.findIndex(item => item.id === selectedItem.value.id)
    if (itemIndex !== -1) {
      cartItems.value[itemIndex] = {
        ...cartItems.value[itemIndex],
        price: roundAmount(tempPriceWithGst.value),
        priceWithoutGst: roundAmount(tempPriceWithoutGst.value),
        priceWithGst: roundAmount(tempPriceWithGst.value),
        tax_rate: tempTaxRate.value
      }
    }
  }
  closePriceModal()
}

const itemTotal = (item) => {
  // item.price is already tax-inclusive (sale_price_inc_tax), so no need to add GST again
  return formatPrice(item.price * item.quantity)
}

const selectCustomer = (customer) => {
  selectedCustomer.value = customer
  closeCustomerModal()
}

const closeCustomerModal = () => {
  showCustomerModal.value = false
  customerSearch.value = ''
  filteredCustomers.value = customers.value
  showAddCustomerForm.value = false
  resetNewCustomerForm()
}

const toggleAddCustomerForm = () => {
  showAddCustomerForm.value = !showAddCustomerForm.value
  if (!showAddCustomerForm.value) {
    resetNewCustomerForm()
  }
}

const resetNewCustomerForm = () => {
  newCustomer.value = {
    name: '',
    mobile_no: '',
    email: '',
    gstin: '',
    customer_type: 'B2C',
    allow_credit: true,
    address: {
      street: '',
      city: '',
      state: authStore.business?.address?.state || '', // Auto-set business state if available
      pincode: ''
    }
  }
  mobileNumberError.value = ''
  gstinError.value = ''
}

const validateMobileNumber = () => {
  const mobile = newCustomer.value.mobile_no
  if (mobile && !/^[0-9]{10}$/.test(mobile)) {
    mobileNumberError.value = 'Mobile number must be exactly 10 digits'
  } else {
    mobileNumberError.value = ''
  }
}

// Contact picker functionality
const selectContactForNewCustomer = async () => {
  try {
    isSelectingContact.value = true
    
    const contacts = await selectContacts({
      properties: ['name', 'tel', 'email'],
      multiple: false
    })
    
    if (contacts && contacts.length > 0) {
      const contact = contacts[0]
      
      // Update form fields
      if (contact.name) newCustomer.value.name = contact.name
      if (contact.phone) newCustomer.value.mobile_no = contact.phone
      if (contact.email) newCustomer.value.email = contact.email
      
      // Validate the mobile number after setting
      validateMobileNumber()
      
      toast.success(`Contact "${contact.name || 'Unknown'}" selected successfully`)
    }
  } catch (error) {
    console.error('Contact selection error:', error)
    
    // Show user-friendly error messages
    if (error.message.includes('not supported')) {
      toast.error('Contact picker is not supported on this device')
    } else if (error.message.includes('cancelled')) {
      toast.info('Contact selection cancelled')
    } else if (error.message.includes('denied')) {
      toast.error('Contact access denied. Please check permissions.')
    } else {
      toast.error(`Failed to select contact: ${error.message}`)
    }
  } finally {
    isSelectingContact.value = false
  }
}

const validateGSTIN = () => {
  const gstin = newCustomer.value.gstin
  if (gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstin)) {
    gstinError.value = 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)'
  } else {
    gstinError.value = ''
  }
}

const onCustomerTypeChange = () => {
  // Clear GSTIN when switching to B2C
  if (newCustomer.value.customer_type === 'B2C') {
    newCustomer.value.gstin = ''
    gstinError.value = ''
  }
}

const saveNewCustomer = async () => {
  if (!newCustomer.value.name.trim() || !newCustomer.value.mobile_no.trim()) {
    toast.error('Name and mobile number are required')
    return
  }

  // Validate mobile number format (10 digits)
  if (!/^[0-9]{10}$/.test(newCustomer.value.mobile_no)) {
    mobileNumberError.value = 'Mobile number must be exactly 10 digits'
    toast.error('Please enter a valid 10-digit mobile number')
    return
  }

  // Validate GSTIN if provided
  if (newCustomer.value.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(newCustomer.value.gstin)) {
    gstinError.value = 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)'
    toast.error('Please enter a valid GSTIN')
    return
  }

  // Clear any validation errors
  mobileNumberError.value = ''
  gstinError.value = ''

  try {
    // Add business_id to the customer data - ensure we get just the string ID
    let businessId = authStore.businessId || authStore.user?.business_id
    
    // If businessId is an object, extract the _id or id field
    if (typeof businessId === 'object' && businessId !== null) {
      businessId = businessId._id || businessId.id
    }
    
    const customerData = {
      ...newCustomer.value,
      business_id: businessId
    }
    
    // console.log('Creating customer with data:', customerData)
    // console.log('Business ID being sent:', businessId, 'Type:', typeof businessId)
    
    const response = await customersApi.createCustomer(customerData)
    // console.log('Customer creation response:', response)
    
    // Extract customer from response (backend returns {message, customer})
    const savedCustomer = response.customer || response
    // console.log('Saved customer:', savedCustomer)
    
    customers.value.push(savedCustomer)
    filteredCustomers.value = customers.value
    
    // Select the newly created customer
    selectCustomer(savedCustomer)
    toast.success('Customer created successfully')
    resetNewCustomerForm()
  } catch (error) {
    console.error('Error creating customer:', error)
    // console.log('Error response data:', error.response?.data)
    // console.log('Error status:', error.response?.status)
    
    if (error.response?.data?.message?.includes('already exists')) {
      toast.error('Customer with this mobile number already exists')
    } else if (error.response?.data?.errors) {
      // Handle validation errors from backend
      const errors = error.response.data.errors
      console.log('Validation errors:', errors)
      errors.forEach(err => {
        if (err.path === 'gstin') {
          gstinError.value = err.msg
        } else if (err.path === 'mobile_no') {
          mobileNumberError.value = err.msg
        }
      })
      toast.error('Please fix the errors above')
    } else if (error.response?.status === 400) {
      toast.error('Invalid data provided. Please check all fields.')
    } else {
      toast.error('Failed to create customer')
    }
  }
}

const editPrice = (item) => {
  editingPrice.value = item
  newPrice.value = item.price
}

const editTax = (item) => {
  editingTax.value = item
  newTaxRate.value = item.tax_rate
}

const savePrice = () => {
  if (editingPrice.value && newPrice.value >= 0) {
    editingPrice.value.price = newPrice.value
    // Removed toast - price change is visible in cart
    // toast.success('Price updated')
  }
  cancelEdit()
}

const saveTax = () => {
  if (editingTax.value && newTaxRate.value >= 0 && newTaxRate.value <= 100) {
    // Only allow tax rate changes when GST is enabled
    editingTax.value.tax_rate = isGstEnabled.value ? newTaxRate.value : 0
    // Removed toast - tax change is visible in cart
    // toast.success('Tax rate updated')
  }
  cancelEdit()
}

const cancelEdit = () => {
  editingPrice.value = null
  editingTax.value = null
  newPrice.value = 0
  newTaxRate.value = 0
}

const toggleBarcodeScanner = () => {
  if (showScannerModal.value) {
    stopBarcodeScanner()
  } else {
    startBarcodeScanner()
  }
}

const startBarcodeScanner = () => {
  showScannerModal.value = true
  
  setTimeout(() => {
    scanner = new Html5QrcodeScanner('barcode-scanner', {
      fps: 20,
      qrbox: { width: 250, height: 150 },
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39
      ],
      supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
      showTorchButtonIfSupported: true,
      showZoomSliderIfSupported: true,
      rememberLastUsedCamera: true,
      aspectRatio: 1.5,
      defaultZoomValueIfSupported: 2
    }, false)
    
    scanner.render((decodedText) => {

      // Play barcode scan sound
      playBarcodeScan()
      
      // Find product by barcode
      const product = products.value.find(p => p.barcode === decodedText)
      if (product) {
        addToCart(product)
        stopBarcodeScanner()
        // Removed toast - addToCart visual feedback is sufficient
        // toast.success(`Product added: ${product.name}`)
      } else {
        toast.warning('Product not found')
        playErrorNotification()
      }
    }, (error) => {
      // Only log significant errors, not routine scan attempts
      if (error && 
          !error.includes('No QR code found') && 
          !error.includes('NotFoundException') &&
          !error.includes('No MultiFormat Readers') &&
          !error.includes('NotFoundError')) {

      }
    })
    

    toast.success('Camera started. Hold barcode steady in the scanning area.')
  }, 100)
}

const stopBarcodeScanner = () => {
  if (scanner) {
    scanner.clear()
    scanner = null
  }
  showScannerModal.value = false
}

// Generate unique transaction reference
const generateTransactionRef = () => {
  const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '').split('.')[0]
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
  return `TXN${timestamp}${random}`
}

// UPI Payment Modal handlers
const closeUpiModal = () => {
  showUpiModal.value = false
  pendingTransactionData.value = null
  pendingTransactionRef.value = ''
}

const handleUpiPaymentReceived = () => {
  if (pendingTransactionData.value) {
    // Process the pending transaction
    completeSale(pendingTransactionData.value)
    closeUpiModal()
  }
}

const handleChangePaymentMethod = () => {
  closeUpiModal()
  // The user can now select a different payment method
  toast.info('Please select a different payment method')
}

// WhatsApp Share Modal handlers
const closeWhatsAppModal = () => {
  showWhatsAppModal.value = false
  completedTransaction.value = null
}

const handleWhatsAppSkip = (data) => {
  if (data.dontShowAgain) {
    // Update business setting to disable WhatsApp sharing
    updateBusinessSetting('whatsapp_invoice_sharing', false)
  }
  closeWhatsAppModal()
}

const handleWhatsAppSent = (data) => {
  toast.success('WhatsApp message opened successfully!')
  if (data.dontShowAgain) {
    // Update business setting to disable WhatsApp sharing
    updateBusinessSetting('whatsapp_invoice_sharing', false)
  }
  closeWhatsAppModal()
}

const handleWhatsAppDontShowAgain = () => {
  updateBusinessSetting('whatsapp_invoice_sharing', false)
  closeWhatsAppModal()
}

// Helper function to update business setting
const updateBusinessSetting = async (setting, value) => {
  try {
    const businessId = authStore.business?._id || authStore.business?.id
    if (businessId) {
      // This would need to be implemented - update business setting via API
      // For now, just show a message
      toast.info('WhatsApp sharing disabled. You can re-enable it in Settings.')
    }
  } catch (error) {
    console.error('Error updating business setting:', error)
  }
}

const processSale = (paymentMethod) => {
  if (cartItems.value.length === 0) {
    toast.warning('Cart is empty')
    return
  }
  
  if (paymentMethod === 'credit' && (!selectedCustomer.value || selectedCustomer.value.id === 'walk-in')) {
    toast.warning('Please select a customer for credit sale')
    return
  }

  // Check if customer allows credit sales
  if (paymentMethod === 'credit' && selectedCustomer.value && !selectedCustomer.value.allow_credit) {
    toast.warning('This customer is not allowed credit sales')
    return
  }
  
  // Handle UPI payment - show QR modal only if VPA is configured
  if (paymentMethod === 'upi') {
    if (!businessVPA.value) {
      // If VPA is not configured, process payment directly like cash/card
      preparePendingTransaction(paymentMethod)
      completeSale(pendingTransactionData.value)
      return
    }
    // Show UPI payment modal when VPA is configured
    pendingTransactionRef.value = generateTransactionRef()
    preparePendingTransaction(paymentMethod)
    showUpiModal.value = true
    return
  }
  
  // For non-UPI payments, process immediately
  preparePendingTransaction(paymentMethod)
  completeSale(pendingTransactionData.value)
}

const preparePendingTransaction = (paymentMethod) => {
  // Create transaction data
  let businessId = authStore.businessId || authStore.user?.business_id
  
  // If businessId is an object, extract the _id or id field
  if (typeof businessId === 'object' && businessId !== null) {
    businessId = businessId._id || businessId.id
  }

  // Map payment method to match backend enum exactly
  const paymentMethodMap = {
    'cash': 'Cash',
    'upi': 'UPI',
    'credit': 'Credit',
    'card': 'Card',
    'advance': 'Advance Balance'
  }
  
  const backendPaymentMethod = paymentMethodMap[paymentMethod] || 'Cash'
  
  const transactionData = {
    business_id: businessId,
    items: cartItems.value.map(item => {
      return {
        product_id: item.id,
        quantity: Number(item.quantity),
        unit_price: Number(item.price),
        mrp: Number(item.mrp || 0) // Include MRP from cart
      }
    }),
    payment_method: backendPaymentMethod,
    discount: Number(discountAmount.value) || 0,
    notes: isGstEnabled.value 
      ? `Subtotal: ₹${subtotal.value}, GST: ₹${formatPrice(gstAmount.value)}, Total: ₹${total.value}`
      : `Total: ₹${total.value}`
  }
  
  // Handle credit sale - set payment status and due amount
  if (paymentMethod === 'credit') {
    transactionData.payment_status = 'pending'
    transactionData.amount_due = total.value
    transactionData.amount_paid = 0
  } else {
    transactionData.payment_status = 'paid'
    transactionData.amount_due = 0
    transactionData.amount_paid = total.value
  }
  
  // Only add customer_id if a valid customer is selected
  if (selectedCustomer.value?._id) {
    transactionData.customer_id = selectedCustomer.value._id
    transactionData.customer_name = selectedCustomer.value.name || 'Walk-in Customer'
    transactionData.customer_mobile = selectedCustomer.value.mobile_no || null
  } else {
    // For walk-in customers
    transactionData.customer_name = 'Walk-in Customer'
    transactionData.customer_mobile = null
  }
  
  // Store the transaction data
  pendingTransactionData.value = transactionData
  
  // Debug: Log transaction data

}

const completeSale = async (transactionData) => {
  try {
    loading.value = true
    
    // Check if online and try to process transaction
    if (navigator.onLine) {
      try {
        const response = await transactionsApi.createTransaction(transactionData)
        
        console.log('✅ Transaction created:', response.transaction?._id)
        console.log('📦 Products sold - Stock should be deducted now')
        transactionData.items.forEach(item => {
          console.log(`  - Product ID: ${item.product_id}, Quantity: ${item.quantity}`)
        })
        

        
        // Play success sound
        playTransactionComplete()
        
        toast.success('Sale completed successfully!')
        
        // Auto-print receipt if enabled
        if (autoprint.value) {
          try {
            await printReceipt(response.transaction)
          } catch (printError) {
            console.error('Auto-print failed:', printError)
            
            if (printerStatus.value.isConnected) {
              toast.warning('Sale completed but thermal printing failed - printer may be disconnected')
            } else {
              toast.warning('Sale completed but printing failed - please connect printer or try web print')
            }
          }
        }
        
        // Check if WhatsApp sharing is enabled and customer is eligible
        const shouldShowWhatsApp = authStore.business?.whatsapp_invoice_sharing && 
                                   isEligibleForWhatsAppShare(response.transaction)
        
        if (shouldShowWhatsApp) {
          // Store completed transaction and show WhatsApp modal
          completedTransaction.value = response.transaction
          showWhatsAppModal.value = true
        }
        
        // Clear cart and reset state
        clearSaleState()
        return
        
      } catch (networkError) {
        console.error('❌ Transaction failed:', networkError.message)
        
        // If it's a network error, queue for offline sync
        if (!navigator.onLine || networkError.code === 'NETWORK_ERROR' || 
            networkError.message?.includes('fetch') || networkError.message?.includes('network')) {

          await handleOfflineTransaction(transactionData)
          return
        }
        
        // If it's a validation or server error, show the error
        throw networkError
      }
    } else {
      // Offline - queue transaction
      await handleOfflineTransaction(transactionData)
      return
    }
    
  } catch (error) {
    console.error('❌ Sale error:', error.message)
    
    // Play error sound
    playErrorNotification()
    
    // Show detailed error message for validation errors
    if (error.response?.data?.errors) {
      const validationErrors = error.response.data.errors.map(err => err.msg || err.message).join(', ')
      toast.error(`Validation errors: ${validationErrors}`)
    } else if (error.response?.data?.message) {
      toast.error(`Error: ${error.response.data.message}`)
    } else {
      toast.error('Failed to complete sale')
    }
  } finally {
    loading.value = false
  }
}

// Handle offline transaction
const handleOfflineTransaction = async (transactionData) => {
  try {
    // Add transaction to offline queue
    const queueId = offlineQueue.queueTransaction(transactionData)
    
    // Update local stock if possible (optimistic update)
    updateLocalStock(transactionData.items)
    
    // Play success sound for offline transaction
    playTransactionComplete()
    
    // Show offline success message
    toast.success('Sale saved offline - will sync when online!', {
      timeout: 5000,
      icon: '🔄'
    })
    
    // Update queue stats
    updateOfflineQueueStats()
    
    // Clear cart and reset state
    clearSaleState()
    
    // Try to print receipt offline using local data
    if (autoprint.value) {
      try {
        const offlineTransaction = {
          ...transactionData,
          _id: queueId,
          invoice_number: `OFFLINE-${Date.now()}`,
          timestamp: new Date().toISOString(),
          status: 'pending_sync'
        }
        await printReceipt(offlineTransaction)
      } catch (printError) {
        console.error('Offline auto-print failed:', printError)
        toast.warning('Sale saved offline but printing failed')
      }
    }
    
  } catch (error) {
    console.error('Error handling offline transaction:', error)
    toast.error('Failed to save transaction offline')
  }
}

// Update local stock quantities (optimistic update)
const updateLocalStock = (transactionItems) => {
  try {
    transactionItems.forEach(item => {
      const product = products.value.find(p => (p.id || p._id) === item.product_id)
      if (product) {
        product.stock_quantity = Math.max(0, (product.stock_quantity || 0) - item.quantity)
      }
    })

  } catch (error) {
    console.error('Error updating local stock:', error)
  }
}

// Clear sale state after completion
const clearSaleState = () => {
  cartItems.value = []
  discount.value = 0
  selectedCustomer.value = null
  
  // Only reload products if online to avoid API errors
  if (navigator.onLine) {
    loadProducts()
  } else {
    console.log('⚠️ Offline mode - skipping product reload')
  }
}

// Update offline queue statistics
const updateOfflineQueueStats = () => {
  try {
    offlineQueueStats.value = offlineQueue.getQueueStats()
  } catch (error) {
    console.error('Error updating offline queue stats:', error)
  }
}

// Sync offline transactions when back online
const syncOfflineTransactions = async () => {
  if (syncInProgress.value) return
  
  try {
    syncInProgress.value = true
    // Only show toast for user-initiated sync, not automatic
    // toast.info('Syncing offline transactions...')
    
    const result = await offlineQueue.syncQueue({ 
      post: (url, data) => {
        if (url === '/transactions') {
          return transactionsApi.createTransaction(data)
        }
        throw new Error(`Unsupported sync URL: ${url}`)
      },
      put: (url, data) => {
        // Handle product/customer updates if needed
        return Promise.resolve()
      }
    })
    
    updateOfflineQueueStats()
    
    // Reload products after successful sync to get updated stock
    if (result.synced > 0) {
      console.log('📦 Reloading products after offline sync...')
      await loadProducts(true) // Force reload
    }
    
    // Only show success toast for multiple transactions or manual sync
    if (result.synced > 1) {
      toast.success(`Successfully synced ${result.synced} transactions`)
    }
    if (result.failed > 0) {
      toast.warning(`${result.failed} transactions failed to sync`)
    }
    
  } catch (error) {
    console.error('Error syncing offline transactions:', error)
    toast.error('Failed to sync offline transactions')
  } finally {
    syncInProgress.value = false
  }
}

// Online/offline event handlers
const handleOnline = () => {
  isOnline.value = true
  toast.info('Back online! Syncing pending transactions...')
  setTimeout(syncOfflineTransactions, 1000) // Delay to ensure connection stability
}

const handleOffline = () => {
  isOnline.value = false
  toast.warning('You are offline. Transactions will be saved locally.')
}

// Retry failed transactions
const retryFailedTransactions = async () => {
  try {
    toast.info('Retrying failed transactions...')
    const result = await offlineQueue.retryFailed({
      post: transactionsApi.createTransaction,
      put: (url, data) => {
        return Promise.resolve()
      }
    })
    
    updateOfflineQueueStats()
    
    if (result && result.synced > 0) {
      toast.success(`Successfully retried ${result.synced} transactions`)
    }
    
  } catch (error) {
    console.error('Error retrying failed transactions:', error)
    toast.error('Failed to retry transactions')
  }
}

// Load app settings and printer configuration
const loadAppSettings = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('appSettings') || '{}')
    
    // Load auto print setting from appSettings (same as SettingsView)
    autoprint.value = settings.autoPrint || false
    // Sync local appSettings with global setting
    appSettings.value.autoPrint = autoprint.value
    
    // Load printer connection if available
    if (settings.connectedPrinter) {
      // Restore printer status (device will need to be reconnected)
      printerStatus.value = {
        ...printerStatus.value,
        isConnected: false, // Always start as disconnected, user needs to reconnect
        lastError: null
      }
    }
    

  } catch (error) {
    console.error('📱 POS: Error loading app settings:', error)
  }
}

// Toggle autoprint setting
const toggleAutoprint = () => {
  autoprint.value = !autoprint.value
  // Sync local appSettings with new value
  appSettings.value.autoPrint = autoprint.value
  
  // Save to localStorage using same key as SettingsView (appSettings.autoPrint)
  try {
    const settings = JSON.parse(localStorage.getItem('appSettings') || '{}')
    settings.autoPrint = autoprint.value
    localStorage.setItem('appSettings', JSON.stringify(settings))
    
    toast.success(`Auto print ${autoprint.value ? 'enabled' : 'disabled'}`)

  } catch (error) {
    console.error('📱 POS: Error saving autoprint setting:', error)
  }
}

// One-click printer connection toggle (matches Transactions view approach)
const togglePrinterConnection = async () => {
  if (printerConnected.value) {
    await disconnectPrinter()
  } else {
    try {
      const devices = await discoverPrinters()
      if (devices.length > 0) {
        await connectToPrinter(devices[0]) // Connect to first available printer (includes SR20 support)
      }
    } catch (error) {
      console.error('Failed to connect printer:', error)
    }
  }
}

// Printer connection is now handled by the usePrinter composable with SR20 support





// Receipt Printing - Thermal and Web Print Available
const printReceipt = async (transaction) => {
  try {
    if (printerStatus.value.isConnected) {
      await printViaThermal(transaction)
    } else {
      await printViaWeb(transaction)
    }
  } catch (error) {
    console.error('Print failed:', error)
    throw new Error('Printing failed: ' + error.message)
  }
}

// Print via thermal printer using templates
const printViaThermal = async (transaction) => {
  if (!printerConnected.value) {
    throw new Error('Printer not connected')
  }
  
  try {

    
    // Get app settings for template selection
    const settings = JSON.parse(localStorage.getItem('appSettings') || '{}')
    const selectedTemplate = settings.templateSettings?.selectedTemplate || 'compact-58'
    

    
    // Prepare business data
    const businessData = {
      name: authStore.business?.name || 'Your Business Name',
      address: authStore.business?.address || {},
      gstin: authStore.business?.gstin || '',
      contact: authStore.business?.contact || {},
      gst_enabled: authStore.business?.gst_enabled || false
    }
    

    
    // Use the composable's template printing method
    await printReceiptWithTemplate(selectedTemplate, transaction, businessData, { 
      customer: transaction.customer || null 
    })
    

    
  } catch (error) {
    console.error('🖨️ Thermal printing failed:', error)
    throw error
  }
}



const printViaWeb = async (transaction) => {
  try {

    
    // Create print window with simple HTML receipt
    const printWindow = window.open('', '_blank', 'width=400,height=600')
    
    const business = authStore.business
    const businessName = business?.business_name || business?.name || 'POS Store'
    const businessAddress = business?.address ? 
      [business.address.street, business.address.city, business.address.state, business.address.pincode]
        .filter(Boolean).join(', ') : ''
    const businessPhone = business?.phone || business?.contact?.phone || ''
    const businessGSTIN = business?.gstin || ''
    
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt</title>
        <style>
          body { 
            font-family: 'Courier New', monospace; 
            margin: 20px; 
            font-size: 12px; 
            line-height: 1.4;
          }
          .header { text-align: center; margin-bottom: 20px; }
          .business-name { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
          .separator { border-top: 1px dashed #000; margin: 10px 0; }
          .item-row { display: flex; justify-content: space-between; margin: 2px 0; }
          .totals { margin-top: 10px; }
          .total-row { display: flex; justify-content: space-between; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="business-name">${businessName.toUpperCase()}</div>
          ${businessAddress ? `<div>${businessAddress}</div>` : ''}
          ${businessPhone ? `<div>Phone: ${businessPhone}</div>` : ''}
          ${businessGSTIN ? `<div>GSTIN: ${businessGSTIN}</div>` : ''}
        </div>
        
        <div class="separator"></div>
        
        <div>
          <div>Invoice: #${transaction.invoice_number || transaction._id || 'N/A'}</div>
          <div>Date: ${new Date(transaction.date || transaction.timestamp || transaction.created_at).toLocaleString('en-IN')}</div>
          ${transaction.customer_name ? `<div>Customer: ${transaction.customer_name}</div>` : ''}
        </div>
        
        <div class="separator"></div>
        
        <div class="items">
          ${transaction.items.map(item => `
            <div class="item-row">
              <span>${item.product_name || item.name || 'Item'}</span>
              <span>₹${(item.total_price || (item.quantity * (item.unit_price || item.price || 0))).toFixed(2)}</span>
            </div>
            <div style="font-size: 10px; color: #666;">
              ${item.quantity} × ₹${(item.unit_price || item.price || 0).toFixed(2)}
            </div>
          `).join('')}
        </div>
        
        <div class="separator"></div>
        
        <div class="totals">
          ${transaction.subtotal ? `
            <div class="item-row">
              <span>Subtotal:</span>
              <span>₹${transaction.subtotal.toFixed(2)}</span>
            </div>
          ` : ''}
          ${transaction.tax_total ? `
            <div class="item-row">
              <span>GST:</span>
              <span>₹${transaction.tax_total.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row">
            <span>TOTAL:</span>
            <span>₹${(transaction.total || transaction.grand_total || 0).toFixed(2)}</span>
          </div>
        </div>
        
        ${transaction.payment_method ? `
          <div class="separator"></div>
          <div class="item-row">
            <span>Payment:</span>
            <span>${transaction.payment_method.toUpperCase()}</span>
          </div>
        ` : ''}
        
        <div class="footer">
          <div>THANK YOU FOR SHOPPING!</div>
          <div>VISIT AGAIN</div>
        </div>
      </body>
      </html>
    `
    
    printWindow.document.write(receiptHTML)
    printWindow.document.close()
    
    if (appSettings.value.webPrintSettings.openDialog) {
      printWindow.focus()
      printWindow.print()
      
      // Close print window after a delay
      setTimeout(() => {
        printWindow.close()
      }, 1000)
    }
    

  } catch (error) {
    throw new Error('Web printing failed: ' + error.message)
  }
}



const generateThermalReceiptHTML = (transaction) => {
  const { receiptSize } = appSettings.value.webPrintSettings
  const paperWidth = receiptSize === 'a4' ? '210mm' : receiptSize === 'thermal' ? '80mm' : '58mm'
  const fontSize = receiptSize === 'a4' ? '12px' : '10px'
  
  // Prepare transaction data
  const businessName = authStore.business?.name || 'POS Store'
  const businessAddress = getBusinessAddress()
  const businessPhone = authStore.business?.contact?.phone || ''
  const businessGSTIN = authStore.business?.gstin || ''
  
  const customerName = transaction.customer_name || 'Walk-in Customer'
  const customerMobile = transaction.customer_mobile || ''
  const customerGSTIN = transaction.customer_gstin || ''
  
  const invoiceNumber = transaction.invoice_number || 'INV-' + Date.now()
  const date = new Date(transaction.timestamp || Date.now()).toLocaleString('en-IN')
  
  // Calculate totals
  const subtotal = transaction.subtotal || calculateSubtotal(transaction.items)
  const taxTotal = transaction.tax_total || 0
  const discount = transaction.discount || 0
  const total = transaction.total || (subtotal + taxTotal - discount)
  
  // GST breakdown
  const isInterStateTransaction = checkInterState()
  const gstBreakdown = calculateGSTBreakdownForReceipt(transaction)
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${isGstEnabled.value ? 'GST Tax Invoice' : 'Bill of Supply'} - ${invoiceNumber}</title>
      <style>
        @page { 
          margin: 5mm; 
          size: ${paperWidth} auto; 
        }
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        body { 
          font-family: 'Courier New', monospace; 
          font-size: ${fontSize}; 
          line-height: 1.3;
          width: 100%;
          background: white;
          color: black;
        }
        .receipt { 
          max-width: ${paperWidth}; 
          margin: 0 auto; 
          padding: 8px;
        }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .large { font-size: ${receiptSize === 'a4' ? '14px' : '12px'}; }
        .small { font-size: ${receiptSize === 'a4' ? '10px' : '8px'}; }
        .text-green { color: #22c55e; font-weight: 600; }
        .line { 
          border-top: 1px dashed #000; 
          margin: 4px 0; 
        }
        .double-line { 
          border-top: 2px solid #000; 
          margin: 4px 0; 
        }
        .flex { 
          display: flex; 
          justify-content: space-between; 
          align-items: flex-start;
        }
        .right { text-align: right; }
        .left { text-align: left; }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 2px 0;
        }
        td { 
          padding: 1px 0; 
          vertical-align: top;
        }
        .item-table td { 
          border-bottom: 1px dotted #ccc; 
          padding: 2px 0;
        }
        .total-row { 
          font-weight: bold; 
          font-size: ${receiptSize === 'a4' ? '13px' : '11px'};
        }
        .gst-breakdown {
          font-size: ${receiptSize === 'a4' ? '9px' : '7px'};
          margin: 2px 0;
        }
        .footer {
          margin-top: 8px;
          font-size: ${receiptSize === 'a4' ? '9px' : '7px'};
        }
        @media print {
          body { 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact;
          }
          .receipt { 
            margin: 0; 
            padding: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        <!-- Header -->
        <div class="center">
          <div class="bold">${businessName}</div>
          ${businessAddress ? '<div class="small">' + businessAddress + '</div>' : ''}
          ${businessPhone ? '<div class="small">Ph: ' + businessPhone + '</div>' : ''}
          ${businessGSTIN ? '<div class="small">GSTIN: ' + businessGSTIN + '</div>' : ''}
        </div>
        
        <div class="line"></div>
        
        <!-- Invoice Details -->
        <div class="center bold">${isGstEnabled.value ? 'GST TAX INVOICE' : 'BILL OF SUPPLY'}</div>
        <table>
          <tr>
            <td><strong>Invoice:</strong> ${invoiceNumber}</td>
            <td class="right"><strong>Date:</strong> ${date}</td>
          </tr>
        </table>
        
        <div class="line"></div>
        
        <!-- Customer Details -->
        <div>
          <strong>Bill To:</strong><br>
          ${customerName}${customerMobile ? '<br>Ph: ' + customerMobile : ''}
          ${customerGSTIN ? '<br>GSTIN: ' + customerGSTIN : ''}
          ${isInterStateTransaction ? '<br><small>Inter-State Transaction</small>' : '<br><small>Intra-State Transaction</small>'}
        </div>
        
        <div class="line"></div>
        
        <!-- Items Table -->
        <table class="item-table">
          <tr>
            <td><strong>Item</strong></td>
            <td class="center"><strong>MRP</strong></td>
            <td class="center"><strong>Qty</strong></td>
            <td class="right"><strong>Rate</strong></td>
            <td class="right"><strong>Amount</strong></td>
          </tr>
          ${transaction.items.map(item => {
            console.log('Printing item:', item.product_name, 'MRP:', item.mrp, 'Unit Price:', item.unit_price, 'Price:', item.price, 'Full item:', item);
            const mrp = item.mrp || 0; // Use actual MRP, don't fallback to price
            // Use unit_price (from transaction) as it's the actual selling price with GST
            const rate = item.unit_price || item.price || 0; 
            const savings = mrp > 0 && rate > 0 ? (mrp - rate) * item.quantity : 0;
            return `
            <tr>
              <td style="max-width: 120px; word-wrap: break-word;">
                ${item.product_name || item.name || 'Item'}
                ${isGstEnabled.value && (item.gst_details?.gst_rate || item.gst_rate) ? '<br><span class="small">GST: ' + (item.gst_details?.gst_rate || item.gst_rate) + '%</span>' : ''}
                ${savings > 0 ? '<br><span class="small text-green">Saved: ₹' + savings.toFixed(2) + '</span>' : ''}
              </td>
              <td class="center">₹${mrp.toFixed(2)}</td>
              <td class="center">${formatQuantity(item.quantity, true, authStore.business?.quantity_decimal_precision)}</td>
              <td class="right">₹${rate.toFixed(2)} <span class="small">(Inc. GST)</span></td>
              <td class="right">₹${(item.total_price || (item.quantity * rate)).toFixed(2)}</td>
            </tr>
          `;
          }).join('')}
        </table>
        
        <div class="line"></div>
        
        <!-- Totals -->
        <table>
          <tr>
            <td><strong>Subtotal:</strong></td>
            <td class="right"><strong>₹${subtotal.toFixed(2)}</strong></td>
          </tr>
          ${(() => {
            const totalSavings = calculateTotalSavings(transaction.items);
            return totalSavings > 0 ? `
          <tr>
            <td style="color: green;"><strong>Total Savings:</strong></td>
            <td class="right" style="color: green;"><strong>₹${totalSavings.toFixed(2)}</strong></td>
          </tr>
          ` : '';
          })()}
          ${discount > 0 ? `
          <tr>
            <td>Discount:</td>
            <td class="right">-₹${discount.toFixed(2)}</td>
          </tr>
          ` : ''}
          
          ${isGstEnabled.value && taxTotal > 0 ? `
          <!-- GST Breakdown -->
          ${isInterStateTransaction ? `
          <tr>
            <td>IGST (${gstBreakdown.rate || 18}%):</td>
            <td class="right">₹${gstBreakdown.igst.toFixed(2)}</td>
          </tr>
          ` : `
          <tr>
            <td>CGST (${(gstBreakdown.rate || 18) / 2}%):</td>
            <td class="right">₹${gstBreakdown.cgst.toFixed(2)}</td>
          </tr>
          <tr>
            <td>SGST (${(gstBreakdown.rate || 18) / 2}%):</td>
            <td class="right">₹${gstBreakdown.sgst.toFixed(2)}</td>
          </tr>
          `}
          <tr>
            <td><strong>Total Tax:</strong></td>
            <td class="right"><strong>₹${taxTotal.toFixed(2)}</strong></td>
          </tr>
          ` : ''}
        </table>
        
        <div class="double-line"></div>
        
        <table class="total-row">
          <tr>
            <td><strong>TOTAL AMOUNT:</strong></td>
            <td class="right"><strong>₹${total.toFixed(2)}</strong></td>
          </tr>
        </table>
        
        <div class="line"></div>
        
        <!-- Payment Details -->
        <table>
          <tr>
            <td><strong>Payment Method:</strong></td>
            <td class="right">${transaction.payment_method || 'Cash'}</td>
          </tr>
          ${transaction.payment_method === 'Credit' ? `
          <tr>
            <td><strong>Amount Due:</strong></td>
            <td class="right">₹${(transaction.amount_due || total).toFixed(2)}</td>
          </tr>
          ` : `
          <tr>
            <td><strong>Amount Paid:</strong></td>
            <td class="right">₹${total.toFixed(2)}</td>
          </tr>
          `}
        </table>
        
        <div class="line"></div>
        
        <!-- Footer -->
        <div class="center footer">
          <div class="bold">Thank You for Your Business!</div>
          <div class="small">Visit Again Soon</div>
          ${authStore.business?.contact?.email ? '<div class="small">' + authStore.business.contact.email + '</div>' : ''}
          <div class="small">${branding.poweredByText.value}</div>
        </div>
        
        <!-- QR Code Space (if needed) -->
        <div style="height: 20px;"></div>
      </div>
    </body>
    </html>
  `
}









// Watch for authentication changes
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  if (isAuthenticated) {
    loadProducts()
    loadCustomers()
  }
}, { immediate: true })

// Watch for GST enabled changes to clean up cart items
watch(() => isGstEnabled.value, (gstEnabled) => {
  if (!gstEnabled) {
    // Clear tax rates from all cart items when GST is disabled
    cartItems.value.forEach(item => {
      item.tax_rate = 0
    })
  }
}, { immediate: false })

// Watch for add customer form opening to set business state as default
// This helps users by pre-selecting their business state for new customers
watch(showAddCustomerForm, (isOpen) => {
  if (isOpen && authStore.business?.address?.state) {
    try {
      // Set customer state to match business state when form opens
      newCustomer.value.address.state = authStore.business.address.state
      console.log('POS: Auto-selected customer state:', authStore.business.address.state)
    } catch (error) {
      console.warn('POS: Failed to auto-select customer state:', error)
    }
  }
})

// Lifecycle
onMounted(() => {
  // Load app settings for print functionality
  loadAppSettings()
  
  // Check contact picker availability
  isContactPickerAvailable.value = isContactPickerSupported()
  
  // Initialize offline functionality
  updateOfflineQueueStats()
  
  // Add online/offline event listeners
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  
  // Listen for service worker sync messages
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'SYNC_COMPLETE') {
        updateOfflineQueueStats()
        const { synced, failed, type } = event.data.data
        if (synced > 0) {
          toast.success(`Background sync: ${synced} ${type} synced successfully`)
        }
        if (failed > 0) {
          toast.warning(`Background sync: ${failed} ${type} failed to sync`)
        }
      } else if (event.data.type === 'SYNC_ERROR') {
        toast.error('Background sync failed')
      }
    })
  }
  
  // Initial load if already authenticated
  if (authStore.isAuthenticated) {
    loadBusinessInfo()
    loadProducts()
    loadCustomers()
    
    // Try to auto-connect to last printer
    try {
      autoConnect()
    } catch (error) {
      console.warn('Auto-connect to printer failed:', error)
    }
  }
  
  // Cleanup event listeners on unmount  
  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  })
})
</script>

<style scoped>
/* Responsive optimizations for small screens */
@media (max-width: 640px) {
  .grid-cols-12 {
    gap: 0.25rem;
  }
  
  .col-span-5 {
    font-size: 0.875rem;
  }
  
  input[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
  
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

/* Hide body overflow when modal is open */
.modal-open {
  overflow: hidden;
}

/* Compact button styles */
.btn-compact {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
}

/* Custom Toast Notification Styles */
.custom-toast-container {
  position: fixed;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  pointer-events: none;
  width: 100%;
  max-width: 600px;
  padding: 0 16px;
}

.custom-toast {
  width: 50%;
  min-width: 280px;
  max-width: 100%;
  padding: 10px 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  pointer-events: auto;
  backdrop-filter: blur(8px);
}

.custom-toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-toast-message {
  flex: 1;
  text-align: center;
  line-height: 1.4;
}

.custom-toast-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.custom-toast-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.custom-toast-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.custom-toast-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .custom-toast {
    width: 60%;
    min-width: 240px;
  }
}
</style>
